import { Badge } from '@oysterjs/ui/badge';
import { Checkbox } from '@oysterjs/ui/checkbox';
import type { ColumnDef } from '@oysterjs/ui/data-table';
import { TableHeader } from '@oysterjs/ui/table';
import React from 'react';
import {
  OutboundCallFieldsFragment,
  OutboundCallSortField,
  OutboundCallSortInput,
  OutboundCallStatus,
  SortDirection
} from '~/api/graphql';
import {
  formatOutboundCallCreatorLabel,
  formatOutboundCallScheduleFromApi,
  formatOutboundCallStatus,
  isOutboundCallActive,
  outboundCallStatusBadgeColor
} from './outbound-call';
import { CallSeenIndicator } from '~/components/calls/call-seen';

export type OutboundCallSortKey =
  | 'customer'
  | 'phone'
  | 'objective'
  | 'status'
  | 'scheduled'
  | 'createdBy';

const SORT_KEY_TO_FIELD: Record<OutboundCallSortKey, OutboundCallSortField> = {
  customer: OutboundCallSortField.Customer,
  phone: OutboundCallSortField.Phone,
  objective: OutboundCallSortField.Objective,
  status: OutboundCallSortField.Status,
  scheduled: OutboundCallSortField.ScheduledAt,
  createdBy: OutboundCallSortField.CreatedBy
};

/** Map UI sort key + direction to GraphQL OutboundCallSortInput (default: SCHEDULED_AT DESC) */
export function outboundCallSortToOutboundCallSortInput(
  sortBy: OutboundCallSortKey | undefined,
  sortDirection: 'asc' | 'desc'
): OutboundCallSortInput {
  const field = sortBy ? SORT_KEY_TO_FIELD[sortBy] : OutboundCallSortField.ScheduledAt;
  const direction = sortDirection === 'asc' ? SortDirection.Asc : SortDirection.Desc;
  return { field, direction };
}
const callObjectiveSummary = (objective: string) => {
  const line =
    objective
      .split('\n')
      .find((part) => part.trim())
      ?.trim() ?? '';
  return line.length > 48 ? `${line.slice(0, 32)}...` : line || 'No objective';
};

export const OutboundCallStatusBadge = (props: { status: OutboundCallStatus }) => (
  <Badge color={outboundCallStatusBadgeColor(props.status)}>
    {formatOutboundCallStatus(props.status)}
  </Badge>
);

export function getOutboundCallColumns(options: {
  selectedSet: Set<string>;
  setSelectedCalls: React.Dispatch<React.SetStateAction<string[]>>;
  filtered: OutboundCallFieldsFragment[];
  /**
   * Drops the selection column when false. Selecting rows only leads to bulk
   * cancel, archive and mark-seen, none of which the caller can perform.
   */
  selectable?: boolean;
}): ColumnDef<OutboundCallFieldsFragment, OutboundCallSortKey>[] {
  const { selectedSet, setSelectedCalls, filtered, selectable = true } = options;

  return [
    ...(selectable
      ? [
          {
            id: 'checkbox',
            noHref: true,
            renderHeader: () => (
              <TableHeader className="w-0 py-1.5 px-3">
                <Checkbox
                  checked={selectedSet.size > 0 && selectedSet.size === filtered.length}
                  color="sky"
                  onChange={(v) => setSelectedCalls(v ? filtered.map((c) => c.id) : [])}
                />
              </TableHeader>
            ),
            renderCell: (call) => (
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  checked={selectedSet.has(call.id)}
                  color="sky"
                  onChange={(v) =>
                    setSelectedCalls((prev) =>
                      v ? [...new Set([...prev, call.id])] : prev.filter((id) => id !== call.id)
                    )
                  }
                />
              </div>
            )
          } satisfies ColumnDef<OutboundCallFieldsFragment, OutboundCallSortKey>
        ]
      : []),
    {
      id: 'customer',
      header: 'Customer',
      sortKey: 'customer',
      renderCell: (row) => (
        <div className="flex items-center">
          {row.latestCall && (
            <div className="mr-2 flex-shrink-0">
              <CallSeenIndicator seenAt={row.latestCall.seenAt} />
            </div>
          )}
          <div>{row.customer.name || 'Unnamed customer'}</div>
        </div>
      )
    },
    {
      id: 'phone',
      header: 'Phone Number',
      sortKey: 'phone',
      renderCell: (row) => row.customer.phone
    },
    {
      id: 'objective',
      header: 'Objective',
      sortKey: 'objective',
      renderCell: (row) => callObjectiveSummary(row.callConfig.callObjective)
    },
    {
      id: 'status',
      header: 'Status',
      sortKey: 'status',
      renderCell: (row) => <OutboundCallStatusBadge status={row.status} />
    },
    {
      id: 'scheduled',
      header: 'Scheduled',
      sortKey: 'scheduled',
      renderCell: (row) =>
        isOutboundCallActive(row.status) ? 'Calling now' : formatOutboundCallScheduleFromApi(row)
    },
    {
      id: 'createdBy',
      header: 'Created by',
      sortKey: 'createdBy',
      renderCell: (row) => (
        <span
          className="block truncate text-sm text-neutral-600 dark:text-neutral-400"
          title={formatOutboundCallCreatorLabel(row.createdBy)}
        >
          {formatOutboundCallCreatorLabel(row.createdBy)}
        </span>
      )
    }
  ];
}

// Retained for callers that sort an already-loaded outbound call collection.
// The paginated list itself uses server-side sorting so ordering is global.
export function sortOutboundCalls(
  data: OutboundCallFieldsFragment[],
  key: OutboundCallSortKey,
  direction: 'asc' | 'desc'
): OutboundCallFieldsFragment[] {
  const sorted = [...data].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'customer':
        cmp = (a.customer.name || '').localeCompare(b.customer.name || '');
        break;
      case 'phone':
        cmp = a.customer.phone.localeCompare(b.customer.phone);
        break;
      case 'objective':
        cmp = a.callConfig.callObjective.localeCompare(b.callConfig.callObjective);
        break;
      case 'status':
        cmp = a.status.localeCompare(b.status);
        break;
      case 'scheduled': {
        const aTime = a.schedule.scheduledAt ? new Date(a.schedule.scheduledAt).getTime() : 0;
        const bTime = b.schedule.scheduledAt ? new Date(b.schedule.scheduledAt).getTime() : 0;
        cmp = aTime - bTime;
        break;
      }
      case 'createdBy':
        cmp = formatOutboundCallCreatorLabel(a.createdBy).localeCompare(
          formatOutboundCallCreatorLabel(b.createdBy)
        );
        break;
      default:
        break;
    }
    return direction === 'asc' ? cmp : -cmp;
  });
  return sorted;
}

export const outboundCallRowHref = (row: OutboundCallFieldsFragment): string =>
  `/app/calls/outbound/${row.id}`;
